import React, { useEffect, useMemo, useRef, useState } from 'react';

import { finleyAssert } from '@finley-technologies/common';
import { DataPipeline } from '@finley-technologies/finley-types';

import { useAllRecentMeshRuns } from '../../hooks/useRecentMeshRuns';
import { UploadDrawerHeader } from './DrawerHeader';
import { SelectExistingMeshRun } from './SelectExistingMeshRun';
import { UploadDrawerSkeletonContent } from './UploadDrawerShell';
import {
  UploadForExistingPipeline,
  UploadForNewPipeline,
} from './UploadForPipeline';
import { SlugToNameProvider } from 'packages/dataPipeline/hooks/slugToNameContext';
import { useLatestMeshDocumentMetadata } from 'packages/dataPipeline/hooks/useLatestMeshDocumentMetadata';
import {
  MeshRunParams,
  RDFV,
  SelectedMeshRun,
} from '~/packages/dataPipeline/types/dataPipelineTypes';

type DrawerState =
  | {
      // drawer is showing the select pipeline tab
      state: 'selectMeshRun';
    }
  | {
      // drawer is showing upload tab for new mesh run
      state: 'uploadNew';
    }
  | {
      // drawer is showing upload tab for existing mesh run
      state: 'uploadExisting';
      selectedMesh: SelectedMeshRun;
    };

const useDrawerState = (
  meshRunId: DataPipeline.MeshRunId | null,
  meshRunParams: MeshRunParams | null,
): [DrawerState, React.Dispatch<React.SetStateAction<DrawerState>>] => {
  const defaultDrawerState: DrawerState = useMemo(() => {
    if (meshRunId) {
      finleyAssert(
        meshRunParams,
        'UploadDrawerInternal:meshRunParams must be provided when meshRunId is provided',
      );
      return {
        state: 'uploadExisting',
        selectedMesh: {
          meshRunId,
          meshRunParams,
        },
      };
    }
    if (meshRunParams) {
      return { state: 'uploadNew' };
    }
    return { state: 'selectMeshRun' };
  }, [meshRunId, meshRunParams]);

  return useState<DrawerState>(defaultDrawerState);
};

/* pipeline drawer lets you choose an existing pipeline or start a new one */
const UploadDrawerContentInternal = ({
  organizationId,
  meshRunId,
  meshDocumentMetadata,
  rdfv,
  latestMeshDocuments,
  allRecentMeshRuns,
  onClose,
  refetchMeshRuns,
}: {
  organizationId: string;
  meshRunId: DataPipeline.MeshRunId | null;
  meshDocumentMetadata: DataPipeline.MeshDocumentMetadata | null;
  rdfv: RDFV | null;
  latestMeshDocuments: DataPipeline.MeshDocumentMetadata[];
  allRecentMeshRuns: DataPipeline.MeshRunRecord[];
  onClose: () => void;
  refetchMeshRuns: () => void;
}) => {
  // if meshDocumentMetadata and rdfv are provided, we lock the drawer to that mesh run
  const [childKey, setChildKey] = useState(0);
  const lockedMeshRun: MeshRunParams | null =
    meshDocumentMetadata && rdfv
      ? {
          meshDocumentMetadata,
          rdfv,
        }
      : null;

  const [drawerState, setDrawerState] = useDrawerState(
    meshRunId,
    lockedMeshRun,
  );

  // are we asked to start with a specific (new or existing) upload?
  const showNewReportButton = !lockedMeshRun;
  const showLatestPipelineButton = !lockedMeshRun;

  // we want to show back button when we are showing upload tab for existing mesh run
  // however, we don't want to show back button when UploadDrawer is opened for a specific pipeline
  // from pipeline. (pipeline details page or verification flow )
  const showBackButton =
    drawerState.state === 'uploadExisting' && !lockedMeshRun;

  const onShowLatestPipelines = () => {
    refetchMeshRuns();
    setDrawerState({ state: 'selectMeshRun' });
  };

  const onNewReport = () => {
    // updating child key to force re-render of the new upload tab
    // this resets the state (date/mesh selection) of the new upload tab
    setChildKey((prevKey) => prevKey + 1);
    setDrawerState({ state: 'uploadNew' });
  };

  const hasInitializedCorrectTab = useRef(false);

  // switch to new upload tab if there are no recent mesh runs
  const numRecentRuns = allRecentMeshRuns.length;
  useEffect(() => {
    if (drawerState.state === 'selectMeshRun' && numRecentRuns === 0) {
      if (hasInitializedCorrectTab.current) {
        // we run this hook only once
        // to switch to new tab if there are no recent mesh runs.
        // if user chooses to go back to to select mesh run tab, we don't want to switch back
        // we will fetch the latest runs when user goes back to select mesh run tab
        return;
      }

      setDrawerState({ state: 'uploadNew' });
      hasInitializedCorrectTab.current = true;
    }
  }, [drawerState.state, numRecentRuns, setDrawerState]);

  return (
    <>
      <UploadDrawerHeader
        showBackButton={showBackButton}
        onBack={onShowLatestPipelines}
        onClose={onClose}
        onNewReport={showNewReportButton ? onNewReport : undefined}
        onShowLatestPipelines={
          showLatestPipelineButton ? onShowLatestPipelines : undefined
        }
      />
      {drawerState.state === 'selectMeshRun' && (
        <SelectExistingMeshRun
          allRecentMeshRuns={allRecentMeshRuns}
          latestMeshDocuments={
            meshDocumentMetadata ? [meshDocumentMetadata] : latestMeshDocuments
          }
          onClose={onClose}
          onPipelineSelected={(selectedMesh) => {
            setDrawerState({
              state: 'uploadExisting',
              selectedMesh,
            });
          }}
        />
      )}
      {drawerState.state === 'uploadNew' && (
        <UploadForNewPipeline
          key={childKey}
          organizationId={organizationId}
          rdfv={rdfv}
          latestMeshDocuments={
            meshDocumentMetadata ? [meshDocumentMetadata] : latestMeshDocuments
          }
          onClose={onClose}
        />
      )}
      {drawerState.state === 'uploadExisting' && (
        <UploadForExistingPipeline
          organizationId={organizationId}
          selectedMeshRun={drawerState.selectedMesh}
          onClose={onClose}
        />
      )}
    </>
  );
};

/**
 *  This control fetches and renders the contents of the drawer data
 *  and passes on to the UploadDrawerInternal for rendering it.
 *  The caller is responsible for putting it inside the shell.
 *  We do this to enable caller to render skeleton insider the drawer
 *  while the data is loading
 */
export const UploadDrawerContent = ({
  organizationId,
  onClose,
  meshRunId,
  meshDocumentMetadata,
  rdfv,
}: {
  organizationId: string;
  onClose: () => void;
  meshRunId: DataPipeline.MeshRunId | null;
  meshDocumentMetadata: DataPipeline.MeshDocumentMetadata | null;
  rdfv: RDFV | null;
}) => {
  const {
    latestMeshDocumentMetadata: latestMeshDocuments,
    loading: loadingLatestMeshDocuments,
  } = useLatestMeshDocumentMetadata({
    organizationId,

    // we will not cause re-render here
    // since caller (drawer) will fetch it and we
    // get cached data.
    forceNetworkFetch: false,
  });

  const {
    recentMeshRuns,
    loading: loadingRecentMeshRuns,
    refetch: refetchMeshRuns,
  } = useAllRecentMeshRuns({
    organizationId,
  });

  if (loadingLatestMeshDocuments || loadingRecentMeshRuns) {
    return <UploadDrawerSkeletonContent onClose={onClose} />;
  }

  // after fetching the data, we render the actual content
  return (
    <SlugToNameProvider
      organizationId={organizationId}
      loadingComponent={<UploadDrawerSkeletonContent onClose={onClose} />}
    >
      <UploadDrawerContentInternal
        organizationId={organizationId}
        onClose={onClose}
        meshRunId={meshRunId}
        meshDocumentMetadata={meshDocumentMetadata}
        rdfv={rdfv}
        allRecentMeshRuns={recentMeshRuns}
        latestMeshDocuments={latestMeshDocuments}
        refetchMeshRuns={refetchMeshRuns}
      />
    </SlugToNameProvider>
  );
};
